/**
 * execReachability — pure "is this node wired to anything live" check.
 *
 * ENG-981 reworked `headTrackedCamera` from a declarative presence-based node
 * into an ordinary exec node: `EventRuntime.js`'s `headTrackedCamera` case
 * only calls `headTrackedCamera.syncFromGraph(node)` when an exec chain
 * actually reaches the node. A merge-gate review found a hole in that
 * guarantee: `App.jsx`'s teardown watcher only re-checked node PRESENCE
 * (`EventRuntime.hasNodeOfType`), not reachability. Deleting the WIRE (not
 * the node) left an armed/tracking webcam session with nothing to disarm
 * it — no exec chain reaches the node to resync it, and the node is still
 * present so the presence-only watcher stayed quiet. Same hole across a
 * character/blueprint switch: `loadBlueprint.js` replaces
 * `eventGraphState.nodes` wholesale, and if the NEW graph's
 * `headTrackedCamera` node (if any) is unwired, the OLD graph's
 * armed/tracking runtime — a module singleton that survives the switch —
 * was never reachable and never got torn down either.
 *
 * This module answers the STRUCTURAL question directly instead of
 * instrumenting the exec walk: is `type`'s node the target of any exec edge,
 * transitively, from some node whose OWN type is a valid chain root
 * (`EVENT_NODE_TYPES`)? Reachability is a property of the GRAPH, not of
 * which event happens to have fired a given pass — a node wired only from
 * `beginPlay` is reachable even while the pass that triggered the recheck
 * was `onConstruct`'s, and must not be punished for a walk that legitimately
 * never reaches it. A node with a deleted/dangling wire, or one fed only
 * from another node that can never itself be fired by `fireEvent`, is not
 * reachable regardless of which pass asks.
 *
 * Disarm rule this module exists to support (see App.jsx's watcher):
 *   - present and reachable from ANY event root -> leave the runtime alone
 *     (it re-syncs itself the next time some exec chain actually runs).
 *   - present but unreachable from every event root, OR absent entirely ->
 *     disarm (`syncFromGraph(null)`).
 *
 * One level into `graphGroup` containers, exactly like graphGroup.js's
 * `findNodeOfType`/`collectGroupStarters` and EventRuntime's own
 * pool-passing execution model: a node inside a group is checked against
 * that group's OWN `(innerNodes, innerEdges)` pool, never mixed with the
 * top-level graph — two groups minted from the same template can carry
 * colliding inner ids, so cross-pool reachability would be a false positive.
 *
 * Pure + import-light (`isExecHandle`, `EVENT_NODE_TYPES`) so this is
 * unit-testable under plain `node --test`, same reasoning as
 * graphGroup.js / eventStarters.js's header comments (EventRuntime.js
 * itself can't be imported there).
 */
import { isExecHandle } from './graphConnectPolicy.js'
import { EVENT_NODE_TYPES } from './eventNodeTypes.js'

/**
 * BFS over exec edges from every `EVENT_NODE_TYPES` root present in `nodes`,
 * within the single pool given by `(nodes, edges)`. Returns the Set of every
 * node id reached (roots included — a root is trivially "reachable").
 */
function reachableIdsInPool(nodes, edges, roots = null) {
  const adj = new Map() // sourceId -> [targetId, ...]
  for (const e of edges) {
    if (!isExecHandle(e.targetHandle)) continue
    let list = adj.get(e.source)
    if (!list) { list = []; adj.set(e.source, list) }
    list.push(e.target)
  }

  const visited = new Set()
  const stack = []
  if (roots) stack.push(...roots)
  else for (const n of nodes) if (EVENT_NODE_TYPES.has(n.type)) stack.push(n.id)

  while (stack.length) {
    const id = stack.pop()
    if (visited.has(id)) continue
    visited.add(id)
    const next = adj.get(id)
    if (next) for (const target of next) stack.push(target)
  }
  return visited
}

/**
 * isNodeTypeReachable(type, nodes, edges) — is the (at most one, per
 * `GLOBAL_UNIQUE_TYPES`) node of `type` reachable from some event root, in
 * its own pool (top-level, or one level into whichever `graphGroup` contains
 * it)?
 *
 * `false` both when the node is absent entirely and when it's present but
 * unreachable — a teardown decision treats the two the same way (disarm), so
 * one boolean covers both. A caller that needs to distinguish "absent" from
 * "present but unreachable" should check presence separately
 * (`EventRuntime.hasNodeOfType` / `findNodeOfType`).
 */
/**
 * isAnyNodeOfTypeReachable(type, nodes, edges) — is there AT LEAST ONE node of
 * `type` reachable from some event root, anywhere in the graph (top level, or
 * one level into any `graphGroup`, and through the body of every reachable
 * `macro`)?
 *
 * The plural sibling of isNodeTypeReachable, which returns on the FIRST node of
 * the type it finds because it was written for the globally-unique types
 * (headTrackedCamera, voxyConfig). A repeatable node needs the whole scan: a
 * graph can hold a `loadEnvironment` on onConstruct AND another on a key press,
 * and "is any of them still wired" is the teardown question — stopping at an
 * unwired one would tear down a feature the graph still drives.
 */
export function isAnyNodeOfTypeReachable(type, nodes, edges) {
  const topReachable = reachableIdsInPool(nodes, edges)
  if (anyReachableIn(type, nodes, edges, topReachable)) return true
  for (const top of nodes) {
    if (top.type === 'graphGroup') {
      const innerNodes = top.data?.innerNodes ?? []
      const innerReachable = reachableIdsInPool(innerNodes, top.data?.innerEdges ?? [])
      if (anyReachableIn(type, innerNodes, top.data?.innerEdges ?? [], innerReachable)) return true
    }
  }
  return false
}

// A node of `type` among `reachable`, or inside the body of a reachable MACRO
// (macroGraph.js). A macro body has no event roots of its own — it runs
// whenever its instance is entered — so its chains are rooted at the Inputs
// tunnel's exec outputs whose matching instance input is wired from a
// reachable node (an unwired input's chain never runs). Recurses, since a
// body may hold another macro.
function anyReachableIn(type, nodes, edges, reachable, depth = 0) {
  for (const n of nodes) {
    if (!reachable.has(n.id)) continue
    if (n.type === type) return true
    if (n.type === 'macro' && depth < 16) {
      const inner = n.data?.innerNodes ?? []
      const innerEdges = n.data?.innerEdges ?? []
      const liveIns = new Set(edges
        .filter(e => e.target === n.id && reachable.has(e.source) && isExecHandle(e.targetHandle)
          && typeof e.targetHandle === 'string' && e.targetHandle.startsWith('execIn:'))
        .map(e => e.targetHandle.slice('execIn:'.length)))
      const tunnelIds = new Set(inner.filter(i => i.type === 'macroInput').map(i => i.id))
      const roots = innerEdges
        .filter(e => tunnelIds.has(e.source) && typeof e.sourceHandle === 'string'
          && e.sourceHandle.startsWith('execOut:') && liveIns.has(e.sourceHandle.slice('execOut:'.length)))
        .map(e => e.target)
      if (roots.length === 0) continue
      const innerReachable = reachableIdsInPool(inner, innerEdges, roots)
      if (anyReachableIn(type, inner, innerEdges, innerReachable, depth + 1)) return true
    }
  }
  return false
}

export function isNodeTypeReachable(type, nodes, edges) {
  for (const top of nodes) {
    if (top.type === type) {
      return reachableIdsInPool(nodes, edges).has(top.id)
    }
    if (top.type === 'graphGroup') {
      const innerNodes = top.data?.innerNodes ?? []
      const inner = innerNodes.find(i => i.type === type)
      if (inner) {
        const innerEdges = top.data?.innerEdges ?? []
        return reachableIdsInPool(innerNodes, innerEdges).has(inner.id)
      }
    }
  }
  return false
}
